import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IcoGrid, IcoSearch, IcoX } from '../../bz/icons';
import BsaEnvironmentIndicator from '../BsaEnvironmentIndicator';
import {
  FACETS,
  SORTS,
  emptySelection,
  facetOptions,
  filterProjects,
  sortProjects,
  vendorLabelOf
} from './projectListModel';
import useProjectListPrefs from './useProjectListPrefs';
import useVersionLabels from './useVersionLabels';
import useAvailableVersions from './useAvailableVersions';
import ProjectFilterRail from './ProjectFilterRail';
import ProjectTable from './ProjectTable';
import ProjectCard from './ProjectCard';
import ExpandGrid from './ExpandGrid';
import ProjectDetail from './ProjectDetail';
import Pager from './Pager';
import './projectsList.css';

const PAGE_SIZE = { list: 25, grid: 12 };

const IcoList = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
  </svg>
);

const SortControl = ({ sort, onChange }) => (
  <span className="pl-sortctl">
    <label className="bz-input pl-select">
      <span className="pl-select__label">Sort</span>
      <select
        value={sort.key}
        onChange={e => onChange({ key: e.target.value, dir: SORTS.find(s => s.key === e.target.value).dir })}
      >
        {SORTS.map(s => (
          <option key={s.key} value={s.key}>
            {s.label}
          </option>
        ))}
      </select>
    </label>
    <button
      type="button"
      className="bz-btn bz-btn--sm pl-dirbtn"
      title={sort.dir === 'asc' ? 'Ascending' : 'Descending'}
      aria-label={sort.dir === 'asc' ? 'Sort ascending' : 'Sort descending'}
      onClick={() => onChange({ ...sort, dir: sort.dir === 'asc' ? 'desc' : 'asc' })}
    >
      {sort.dir === 'asc' ? '↑' : '↓'}
    </button>
  </span>
);
SortControl.propTypes = { sort: PropTypes.object.isRequired, onChange: PropTypes.func.isRequired };

const ViewToggle = ({ view, onChange }) => (
  <div className="pl-seg" role="group" aria-label="View">
    <button
      type="button"
      className={view === 'list' ? 'is-on' : ''}
      title="Table view"
      aria-pressed={view === 'list'}
      onClick={() => onChange('list')}
    >
      <IcoList />
    </button>
    <button
      type="button"
      className={view === 'grid' ? 'is-on' : ''}
      title="Card view"
      aria-pressed={view === 'grid'}
      onClick={() => onChange('grid')}
    >
      <IcoGrid size={14} />
    </button>
  </div>
);
ViewToggle.propTypes = { view: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

// "/" focuses search. While a project is expanded: ↑/↓ (j/k) move, Enter opens,
// Esc collapses — but never while typing, renaming, or when Enter is aimed at a control.
function useListKeyboard({ searchRef, expanded, items, setExpandedId, onOpen, paused }) {
  // One window listener for the list's lifetime; it reads the current state
  // through a ref instead of re-subscribing on every render.
  const latest = useRef({});
  latest.current = { searchRef, expanded, items, setExpandedId, onOpen, paused };

  useEffect(() => {
    const onKey = e => {
      const { searchRef, expanded, items, setExpandedId, onOpen, paused } = latest.current;
      if (e.defaultPrevented || paused) return;
      // Leave browser/OS shortcuts alone, and never act behind an open dialog (e.g. Delete).
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const focused = document.activeElement;
      if (document.querySelector('.modal.show') || (focused && focused.closest && focused.closest('[role="dialog"]'))) {
        return;
      }
      const tag = document.activeElement && document.activeElement.tagName;
      const typing = tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA';
      if (e.key === '/' && !typing && searchRef.current) {
        e.preventDefault();
        searchRef.current.focus();
        return;
      }
      if (!expanded || typing) return;
      const index = items.findIndex(p => p.id === expanded.id);
      if (e.key === 'Escape') setExpandedId(null);
      else if ((e.key === 'ArrowDown' || e.key === 'j') && index < items.length - 1) setExpandedId(items[index + 1].id);
      else if ((e.key === 'ArrowUp' || e.key === 'k') && index > 0) setExpandedId(items[index - 1].id);
      else if (e.key === 'Enter' && tag !== 'BUTTON' && tag !== 'A') onOpen(expanded);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const expandedId = expanded ? expanded.id : null;
  useEffect(() => {
    if (!expandedId) return;
    const panel = document.querySelector('.pl-xrow, .pl-xstrip');
    if (panel) panel.scrollIntoView({ block: 'nearest' });
    const item = document.querySelector(`[data-pid="${expandedId}"]`);
    if (item) item.scrollIntoView({ block: 'nearest' });
  }, [expandedId]);
}

const ProjectsList = ({
  projects,
  vendors,
  currentUserEmail,
  canModify,
  onOpen,
  onCreate,
  onRename,
  onExport,
  onRefresh,
  onDelete
}) => {
  const { view, sort, setView, setSort } = useProjectListPrefs();
  const [query, setQuery] = useState('');
  const [selection, setSelection] = useState(emptySelection);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const searchRef = useRef(null);
  const versionLabel = useVersionLabels(projects);
  const availableVersions = useAvailableVersions(projects);

  const ctx = {
    vendors,
    currentUserEmail,
    vendorLabel: id => vendorLabelOf(id, vendors),
    versionLabel,
    availableVersions,
    availableLoading: projects.some(p => availableVersions(p) === undefined)
  };

  useEffect(() => setPage(1), [query, selection, sort, view]);

  const filtered = sortProjects(filterProjects(projects, query, selection, ctx), sort, ctx);
  const pageSize = PAGE_SIZE[view] || PAGE_SIZE.list;
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = Math.min(page, pages);
  const from = (current - 1) * pageSize;
  const pageItems = filtered.slice(from, from + pageSize);
  const expanded = pageItems.find(p => p.id === expandedId) || null;

  useListKeyboard({ searchRef, expanded, items: pageItems, setExpandedId, onOpen, paused: editingId !== null });

  const actions = {
    toggle: project => setExpandedId(currentId => (currentId === project.id ? null : project.id)),
    open: project => onOpen(project),
    startRename: project => setEditingId(project.id),
    cancelRename: () => setEditingId(null),
    saveName: async (project, name) => {
      setEditingId(null);
      await onRename(project, name);
    },
    exportReport: project => onExport(project),
    refresh: project => onRefresh(project),
    remove: project => onDelete(project),
    collapse: () => setExpandedId(null)
  };

  const toggleFacet = (key, id) =>
    setSelection(s => ({ ...s, [key]: s[key].includes(id) ? s[key].filter(x => x !== id) : [...s[key], id] }));
  const clearAll = () => {
    setSelection(emptySelection());
    setQuery('');
  };
  const chips = FACETS.flatMap(f =>
    selection[f.key].map(id => {
      const option = facetOptions(f.key, projects, ctx).find(o => o.id === id);
      return { key: f.key, id, label: option ? option.label : id };
    })
  );

  const header = (
    <div className="bz-section-head pl-head">
      <div>
        <div className="bz-kicker pl-head__kicker">Plan · Projects</div>
        <h1 className="bz-h1 pl-head__title">
          Projects <span className="pl-head__count">{projects.length}</span>
        </h1>
        <div className="bz-muted pl-head__sub">
          Compare product versions by reviewing security and operational risks
        </div>
      </div>
      <div className="bz-row pl-head__actions">
        <BsaEnvironmentIndicator />
        {canModify && (
          <button type="button" className="bz-btn bz-btn--primary" onClick={onCreate}>
            + New project
          </button>
        )}
      </div>
    </div>
  );

  if (projects.length === 0) {
    return (
      <div className="pl-page">
        {header}
        <div className="pl-empty">
          <div className="pl-empty__title">No projects yet</div>
          <div className="bz-muted">Create a project from your inventory or pick a vendor, product and version.</div>
          {canModify && (
            <button type="button" className="bz-btn bz-btn--primary" onClick={onCreate}>
              + Create your first project
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pl-page">
      {header}
      <div className="pl-layout">
        <ProjectFilterRail
          projects={projects}
          query={query}
          selection={selection}
          ctx={ctx}
          onToggle={toggleFacet}
          onClear={clearAll}
        />
        <div className="pl-main">
          <div className="pl-toolbar">
            <label className="bz-input pl-search">
              <IcoSearch size={14} />
              <input
                ref={searchRef}
                value={query}
                placeholder="Search projects, products, versions…"
                aria-label="Search projects"
                onChange={e => setQuery(e.target.value)}
              />
              {query ? (
                <button
                  type="button"
                  className="pl-search__clear"
                  aria-label="Clear search"
                  onClick={() => setQuery('')}
                >
                  <IcoX size={12} />
                </button>
              ) : (
                <span className="pl-kbd">/</span>
              )}
            </label>
            <span className="bz-muted pl-count">
              {filtered.length === projects.length
                ? `${projects.length} project${projects.length === 1 ? '' : 's'}`
                : `${filtered.length} of ${projects.length} project${projects.length === 1 ? '' : 's'}`}
            </span>
            <span className="bz-grow" />
            <SortControl sort={sort} onChange={setSort} />
            <ViewToggle view={view} onChange={setView} />
          </div>

          {chips.length > 0 && (
            <div className="pl-chips">
              {chips.map(chip => (
                <button
                  key={`${chip.key}:${chip.id}`}
                  type="button"
                  className="pl-chip"
                  onClick={() => toggleFacet(chip.key, chip.id)}
                >
                  {chip.label} <span aria-hidden="true">×</span>
                </button>
              ))}
              <button type="button" className="pl-linkbtn" onClick={clearAll}>
                Clear all
              </button>
            </div>
          )}

          <div className="pl-hint">Click a project for details · click its name or press Enter to open it</div>

          {filtered.length === 0 ? (
            <div className="pl-empty">
              <div className="pl-empty__title">No projects match</div>
              <div className="bz-muted">Loosen a filter or clear the search.</div>
              <button type="button" className="bz-btn" onClick={clearAll}>
                Clear all filters
              </button>
            </div>
          ) : view === 'list' ? (
            <ProjectTable
              projects={pageItems}
              ctx={ctx}
              sort={sort}
              onSort={setSort}
              expandedId={expandedId}
              editingId={editingId}
              canModify={canModify}
              actions={actions}
            />
          ) : (
            <ExpandGrid
              items={pageItems}
              expandedId={expandedId}
              renderCard={project => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  ctx={ctx}
                  expanded={project.id === expandedId}
                  editing={project.id === editingId}
                  canModify={canModify}
                  actions={actions}
                />
              )}
              renderDetail={project => (
                <ProjectDetail project={project} ctx={ctx} canModify={canModify} actions={actions} />
              )}
            />
          )}

          {filtered.length > 0 && (
            <Pager
              page={current}
              pages={pages}
              from={from + 1}
              to={from + pageItems.length}
              total={filtered.length}
              onPage={setPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

ProjectsList.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.object).isRequired,
  vendors: PropTypes.arrayOf(PropTypes.object),
  currentUserEmail: PropTypes.string,
  canModify: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

export default ProjectsList;
